/**
 * Effort Matrix data layer (task 13.2, R2, R7.4, R7.7, R7.8).
 *
 * Wired to `/api/manpower/effort-assumptions`, which serves Effort_Assumption
 * records (effort per unit in minutes, fixed overhead hours) by Activity Type,
 * FF role and population unit. Each combination may have multiple versions; the
 * screen surfaces all versions and the mandatory recalculation warning (R2.5)
 * before a new version persists.
 */
import {
  effortAssumptionListResponseSchema,
  effortAssumptionSchema,
  type EffortAssumption,
  type EffortAssumptionListResponse,
  type EffortAssumptionWrite,
} from '@complan/contracts';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiFetch } from '@/shared/api/client';
import { manpowerQueryKeys } from '../manpower-query-keys';

/** Fetches every Effort_Assumption record, unpaged (R9.3's volumetric baseline). */
export async function fetchEffortAssumptions(): Promise<EffortAssumptionListResponse> {
  const body = await apiFetch<unknown>('/api/manpower/effort-assumptions');
  return effortAssumptionListResponseSchema.parse(body);
}

/** React Query hook for the Effort_Assumption list (R2.1, R2.6). */
export function useEffortAssumptions(): UseQueryResult<EffortAssumptionListResponse, unknown> {
  return useQuery({
    queryKey: manpowerQueryKeys.effortAssumptions(),
    queryFn: fetchEffortAssumptions,
  });
}

/**
 * Creates an Effort_Assumption version (R2.1, R2.2, R2.5, R2.6).
 *
 * Every submission is a new version for a combination (R2.2), and every
 * submission after the first requires acknowledgement of the recalculation
 * warning (R2.5). A rule rejection comes back as `validation_failed` the
 * caller surfaces from the thrown error.
 *
 * On success, invalidates all four Manpower screens (task 14.4), since an
 * Effort_Assumption change can alter demand calculations on Capacity &
 * Utilisation and Demand Explorer as well as the Effort Matrix itself.
 */
export function useCreateEffortAssumption(): UseMutationResult<
  EffortAssumption,
  unknown,
  EffortAssumptionWrite
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: EffortAssumptionWrite) => {
      const body = await apiFetch<unknown>('/api/manpower/effort-assumptions', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return effortAssumptionSchema.parse(body);
    },
    onSuccess: () => {
      // Invalidate all Manpower screens (task 14.4, R7.1, R7.2, R7.3, R7.4)
      void queryClient.invalidateQueries({ queryKey: manpowerQueryKeys.all });
    },
  });
}

/**
 * Deactivates an Effort_Assumption version (R2.8). Deactivation is blocked when
 * any Field_Force_Demand_Driver with a future or open-ended calculation date
 * references this combination — the server returns a `validation_failed` with
 * the exact count, which the caller surfaces.
 *
 * On success, invalidates all four Manpower screens (task 14.4).
 */
export function useDeactivateEffortAssumption(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiFetch<unknown>(`/api/manpower/effort-assumptions/${id}/deactivate`, {
        method: 'POST',
        body: JSON.stringify({ acknowledgedWarningRuleIds: [] }),
      });
    },
    onSuccess: () => {
      // Invalidate all Manpower screens (task 14.4, R7.1, R7.2, R7.3, R7.4)
      void queryClient.invalidateQueries({ queryKey: manpowerQueryKeys.all });
    },
  });
}

